import { useMemo, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { ClipboardCopy } from "lucide-react";
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Textarea,
  toast,
} from "@lumika/ui";
import { adminApi } from "../../api/admin";
import { IMPORT_SCHEMA_TEXT, parseProspectRows } from "./import-parse";

async function copyImportSchema(): Promise<void> {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(IMPORT_SCHEMA_TEXT);
    } else {
      // Fallback for non-secure (HTTP) contexts where the async Clipboard API
      // isn't exposed — select a detached textarea and copy the old way.
      const ta = document.createElement("textarea");
      ta.value = IMPORT_SCHEMA_TEXT;
      ta.style.position = "fixed";
      ta.style.opacity = "0";
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      document.body.removeChild(ta);
    }
    toast.success("Schema copied — paste it into ChatGPT or Claude to collect prospects.");
  } catch {
    toast.error("Couldn't copy the schema — try selecting the textarea contents manually.");
  }
}

export function ImportProspectsDialog({ onClose }: { onClose: () => void }) {
  const qc = useQueryClient();
  const [text, setText] = useState("");
  const parsed = useMemo(() => parseProspectRows(text), [text]);

  const importMutation = useMutation({
    mutationFn: () => adminApi.importOutreachProspects(parsed.rows),
    onSuccess: async (result) => {
      await qc.invalidateQueries({ queryKey: ["outreach"] });
      toast.success(`Imported ${result.created} prospect${result.created === 1 ? "" : "s"}`);
      onClose();
    },
    onError: (error: Error) => toast.error(error.message),
  });

  const preview = parsed.rows.slice(0, 8);

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Import prospects</DialogTitle>
          <DialogDescription>
            Paste rows from a spreadsheet (tab-separated) or CSV with a header row — or just
            paste one company name per line.
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-4">
          <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-dashed bg-muted/30 px-3 py-2">
            <p className="text-xs text-muted-foreground">
              Collecting prospects with AI? Copy the schema and hand it to your assistant.
            </p>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="shrink-0 gap-1.5"
              onClick={copyImportSchema}
            >
              <ClipboardCopy className="h-3.5 w-3.5" />
              Copy schema
            </Button>
          </div>
          <Textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            rows={6}
            autoFocus
            aria-label="Prospect rows to import"
            placeholder={"company,email,city\nSunshine Auto,gm@sunshine.com,Miami"}
            className="font-mono text-xs"
          />
          <p className="text-xs text-muted-foreground">
            Columns: company (required), contactName, contactTitle, email, phone, website,
            linkedin, city, state, source, stage, demoAt, followUp, tags.
          </p>
          {parsed.rows.length > 0 && (
            <div className="grid gap-2">
              <p className="text-sm text-muted-foreground">
                {parsed.rows.length} ready to import
                {parsed.skipped > 0 && ` · ${parsed.skipped} skipped (no company or invalid email)`}
              </p>
              <div className="max-h-56 overflow-auto rounded-lg border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Company</TableHead>
                      <TableHead>Contact</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>Stage</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {preview.map((row, i) => (
                      <TableRow key={`${row.companyName}-${i}`}>
                        <TableCell className="font-medium">{row.companyName}</TableCell>
                        <TableCell className="text-muted-foreground">
                          {row.contactName ?? "—"}
                        </TableCell>
                        <TableCell className="text-muted-foreground">{row.email ?? "—"}</TableCell>
                        <TableCell className="text-muted-foreground">{row.stage}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
              {parsed.rows.length > preview.length && (
                <p className="text-xs text-muted-foreground">
                  …and {parsed.rows.length - preview.length} more.
                </p>
              )}
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={() => importMutation.mutate()}
            disabled={parsed.rows.length === 0 || importMutation.isPending}
          >
            {importMutation.isPending
              ? "Importing…"
              : `Import ${parsed.rows.length || ""}`.trim()}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
